import type { FC, ReactNode } from 'react'
import type { JetLagDay } from '@/lib/circadian-planner'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'

import {
    Coffee,
    EyeOff,
    Info,
    Moon,
    Plane,
    Printer,
    Sun
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { printArea, removePrintFrames } from '@/lib/print-area'

import {
    CITIES,
    CITY_REGIONS,
    EAST_RATE_H,
    JET_LAG_MIN_H,
    PREP_DAY_OPTIONS,
    WEST_RATE_H,
    buildJetLagPlan,
    formatClock,
    formatHours,
    parseTime,
    zoneOffsetMinutes
} from '@/lib/circadian-planner'

const inputClass =
    'mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring'

const chipClass = (selected: boolean): string =>
    cn(
        'rounded-full border px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        selected
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-border bg-background text-foreground hover:bg-secondary'
    )

const DAYS_AHEAD = 14

const isoDate = (date: Date): string =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`

const range = (start: number, end: number): string => `${formatClock(start)} to ${formatClock(end)}`

interface CitySelectProps {
    id: string
    label: string
    value: string
    onChange: (value: string) => void
}

const CitySelect: FC<CitySelectProps> = ({ id, label, value, onChange }): ReactNode => (
    <div>
        <label htmlFor={id} className='block text-xs font-medium text-muted-foreground'>
            {label}
        </label>
        <select id={id} value={value} onChange={(event) => onChange(event.target.value)} className={inputClass}>
            {CITY_REGIONS.map((region) => (
                <optgroup key={region} label={region}>
                    {CITIES.filter((city) => city.region === region).map((city) => (
                        <option key={city.id} value={city.id}>{city.name}</option>
                    ))}
                </optgroup>
            ))}
        </select>
    </div>
)

interface DayCardProps {
    day: JetLagDay
}

const DayCard: FC<DayCardProps> = ({ day }): ReactNode => (
    <span className='block break-inside-avoid rounded-xl border border-border bg-background px-4 py-4'>
        <span className='flex flex-wrap items-baseline justify-between gap-2'>
            <span className='font-heading text-base font-semibold text-foreground'>{day.label}</span>
            <span className='text-xs text-muted-foreground'>
                {day.phase === 'prep' ? 'At home, home time' : 'Local time'}
            </span>
        </span>

        <span className='mt-3 flex flex-col gap-2 text-sm text-foreground'>
            <span className='flex gap-2'>
                <Moon className='mt-0.5 size-4 shrink-0 text-primary' />
                <span>
                    <span className='font-medium'>Sleep:</span> {range(day.bed, day.wake)}
                </span>
            </span>
            <span className='flex gap-2'>
                <Sun className='mt-0.5 size-4 shrink-0 text-primary' />
                <span>
                    <span className='font-medium'>Get bright light:</span>{' '}
                    {day.seekFallback ?? `${range(day.seek!.start, day.seek!.end)}, outdoors if you can.`}
                </span>
            </span>
            {day.avoid && (
                <span className='flex gap-2'>
                    <EyeOff className='mt-0.5 size-4 shrink-0 text-primary' />
                    <span>
                        <span className='font-medium'>Avoid bright light:</span> {range(day.avoid.start, day.avoid.end)}.
                        Stay indoors or in the shade, and wear sunglasses outside.
                    </span>
                </span>
            )}
            <span className='flex gap-2'>
                <Coffee className='mt-0.5 size-4 shrink-0 text-primary' />
                <span>
                    <span className='font-medium'>Caffeine:</span> fine in the morning, last one by{' '}
                    {formatClock(day.caffeineCutoff)}.
                </span>
            </span>
        </span>

        {day.notes.length > 0 && (
            <span className='mt-3 block text-xs leading-relaxed text-muted-foreground'>{day.notes.join(' ')}</span>
        )}
    </span>
)

export const JetLagPlanner: FC = (): ReactNode => {
    const [from, setFrom] = useState('new-york')
    const [to, setTo] = useState('london')
    const [date, setDate] = useState('')
    const [bed, setBed] = useState('23:00')
    const [wake, setWake] = useState('07:00')
    const [prepDays, setPrepDays] = useState(2)

    const printRef = useRef<HTMLDivElement>(null)

    // The travel date defaults to two weeks out. Set after mount, because the
    // server does not know the visitor's date.
    useEffect(() => {
        const soon = new Date()
        soon.setDate(soon.getDate() + DAYS_AHEAD)
        setDate(isoDate(soon))

        return removePrintFrames
    }, [])

    const home = CITIES.find((city) => city.id === from)
    const away = CITIES.find((city) => city.id === to)
    const bedMin = parseTime(bed)
    const wakeMin = parseTime(wake)

    // Offsets are read for the travel date so daylight saving time counts.
    const when = /^\d{4}-\d{2}-\d{2}$/.test(date) ? new Date(`${date}T12:00:00Z`) : null
    const differenceH =
        home && away && when ? (zoneOffsetMinutes(away.zone, when) - zoneOffsetMinutes(home.zone, when)) / 60 : null

    const plan =
        differenceH !== null && bedMin !== null && wakeMin !== null
            ? buildJetLagPlan({ differenceH, bed: bedMin, wake: wakeMin, prepDays })
            : null

    const geographicEast = plan ? plan.differenceH > 0 : false
    const reversed = plan ? (geographicEast ? plan.direction === 'delay' : plan.direction === 'advance') : false
    const totalDays = plan ? Math.ceil(plan.shiftH / (plan.direction === 'advance' ? EAST_RATE_H : WEST_RATE_H) - 1e-9) : 0

    return (
        <section
            aria-label='Jet lag planner'
            className='my-10 overflow-hidden rounded-2xl border border-border bg-card shadow-sm print:border-0 print:shadow-none'
        >
            <div className='border-b border-border bg-secondary/50 px-5 py-4 sm:px-6'>
                <div className='flex items-center gap-2'>
                    <Plane className='size-5 shrink-0 text-primary' />
                    <span className='font-heading text-lg font-semibold tracking-tight text-foreground'>
                        Jet lag planner
                    </span>
                </div>
                <span className='mt-1 block text-sm text-muted-foreground'>
                    Pick where you are flying from and to, and get a day-by-day plan for sleep, light and caffeine.
                </span>
            </div>

            <div className='px-5 py-5 sm:px-6'>
                <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                    <CitySelect id='jetlag-from' label='Flying from' value={from} onChange={setFrom} />
                    <CitySelect id='jetlag-to' label='Flying to' value={to} onChange={setTo} />
                </div>
                <span className='mt-2 block text-xs text-muted-foreground'>
                    City not listed? Pick one in the same time zone.
                </span>

                <div className='mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3'>
                    <div>
                        <label htmlFor='jetlag-date' className='block text-xs font-medium text-muted-foreground'>
                            Travel date
                        </label>
                        <input
                            id='jetlag-date'
                            type='date'
                            value={date}
                            onChange={(event) => setDate(event.target.value)}
                            className={inputClass}
                        />
                    </div>
                    <div>
                        <label htmlFor='jetlag-bed' className='block text-xs font-medium text-muted-foreground'>
                            Usual bedtime
                        </label>
                        <input
                            id='jetlag-bed'
                            type='time'
                            value={bed}
                            onChange={(event) => setBed(event.target.value)}
                            className={inputClass}
                        />
                    </div>
                    <div>
                        <label htmlFor='jetlag-wake' className='block text-xs font-medium text-muted-foreground'>
                            Usual wake time
                        </label>
                        <input
                            id='jetlag-wake'
                            type='time'
                            value={wake}
                            onChange={(event) => setWake(event.target.value)}
                            className={inputClass}
                        />
                    </div>
                </div>

                <div className='mt-5'>
                    <span id='jetlag-prep-label' className='block text-xs font-medium text-muted-foreground'>
                        Days you can start adjusting before you fly
                    </span>
                    <div role='radiogroup' aria-labelledby='jetlag-prep-label' className='mt-2 flex flex-wrap gap-2'>
                        {PREP_DAY_OPTIONS.map((option) => (
                            <button
                                key={option}
                                type='button'
                                role='radio'
                                aria-checked={prepDays === option}
                                onClick={() => setPrepDays(option)}
                                className={chipClass(prepDays === option)}
                            >
                                {option === 0 ? 'None' : option === 1 ? '1 day' : `${option} days`}
                            </button>
                        ))}
                    </div>
                    <span className='mt-2 block text-xs text-muted-foreground'>
                        The CDC suggests 2 to 3 days. Each one moves your sleep an hour, so it can clash with work or family plans.
                    </span>
                </div>

                <div aria-live='polite' className='mt-6'>
                    {!plan || differenceH === null ? (
                        <span className='block rounded-xl border border-border bg-background px-4 py-4 text-sm text-muted-foreground'>
                            Choose both cities, a travel date and your usual sleep times to see your plan.
                        </span>
                    ) : plan.minor ? (
                        <span className='block rounded-xl border border-primary/40 bg-primary/5 px-4 py-4 text-sm leading-relaxed text-foreground'>
                            {Math.abs(plan.differenceH) === 0
                                ? "Both cities are on the same time on that date, so there is no time difference to adjust to."
                                : `The time difference is ${formatHours(Math.abs(plan.differenceH))}. Jet lag usually follows crossing at least ${JET_LAG_MIN_H} time zones, so you are unlikely to need a plan.`}{' '}
                            Keep your usual bedtime on local time and get daylight in the morning.
                        </span>
                    ) : (
                        <div data-print-area ref={printRef}>
                            <span className='mb-4 hidden text-sm text-muted-foreground print:block'>
                                Made with the free jet lag planner from WellPath Fusion. {home?.name} to {away?.name}.
                            </span>

                            <div className='rounded-xl border border-primary/40 bg-primary/5 px-4 py-4'>
                                <span className='block text-sm font-medium text-foreground'>
                                    {away?.name} is {formatHours(Math.abs(plan.differenceH))}{' '}
                                    {geographicEast ? 'ahead of' : 'behind'} {home?.name}
                                </span>
                                <span className='mt-1 block font-heading text-3xl font-semibold tracking-tight text-foreground'>
                                    {plan.arrivalDays === 0
                                        ? 'Adjusted before you land'
                                        : `About ${plan.arrivalDays} ${plan.arrivalDays === 1 ? 'day' : 'days'} to adjust`}
                                </span>
                                <span className='mt-2 block text-sm leading-relaxed text-muted-foreground'>
                                    {reversed
                                        ? `For a change this large, moving your body clock ${formatHours(plan.shiftH)} ${plan.direction === 'delay' ? 'later' : 'earlier'} is quicker than going the other way round the clock, so this plan does that. `
                                        : `Your body clock needs to move ${formatHours(plan.shiftH)} ${plan.direction === 'advance' ? 'earlier' : 'later'}. `}
                                    {`On average it shifts about ${formatHours(plan.direction === 'advance' ? EAST_RATE_H : WEST_RATE_H)} a day ${plan.direction === 'advance' ? 'earlier' : 'later'}, so that takes about ${totalDays} ${totalDays === 1 ? 'day' : 'days'}`}
                                    {plan.prepShiftH > 0
                                        ? `. Shifting ${formatHours(plan.prepShiftH)} before you fly leaves about ${plan.arrivalDays} ${plan.arrivalDays === 1 ? 'day' : 'days'} at your destination.`
                                        : '.'}
                                </span>
                            </div>

                            <span className='mt-5 grid gap-3 sm:grid-cols-2'>
                                {plan.days.map((day) => (
                                    <DayCard key={day.key} day={day} />
                                ))}
                            </span>

                            <span className='mt-3 block text-sm leading-relaxed text-muted-foreground'>
                                After that, your body clock should be close to local time. Keep a regular bedtime and get daylight in the morning.
                            </span>

                            <span className='mt-4 hidden text-xs leading-relaxed text-muted-foreground print:block'>
                                A general plan built on average adjustment rates, not medical advice. Ask a doctor or
                                pharmacist before using melatonin or sleep medicine for travel.
                            </span>

                            <span className='mt-5 flex flex-wrap gap-3 print:hidden'>
                                <button
                                    type='button'
                                    onClick={() => printRef.current && printArea(printRef.current, 'Your jet lag plan | WellPath Fusion')}
                                    className='inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
                                >
                                    <Printer className='size-4' />
                                    Print or save as PDF
                                </button>
                            </span>
                        </div>
                    )}
                </div>

                <div className='mt-5 flex gap-2 rounded-xl border border-border bg-secondary/40 px-4 py-3'>
                    <Info className='mt-0.5 size-4 shrink-0 text-muted-foreground' />
                    <span className='text-xs leading-relaxed text-muted-foreground'>
                        A general plan built on average adjustment rates, not medical advice. Nothing you enter is
                        stored. Melatonin and sleep medicine help some travelers, but taking them at the wrong time can
                        make jet lag worse, so ask a doctor or pharmacist first. See{' '}
                        <Link href='/sleep/circadian-rhythm-101' className='font-normal text-inherit underline underline-offset-2 hover:text-foreground'>
                            how your circadian rhythm works
                        </Link>
                        .
                    </span>
                </div>
            </div>
        </section>
    )
}

export default JetLagPlanner
